import { defineStore } from 'pinia';
import { ref } from 'vue';
import { categoriesCollection, metadataCollection } from '../firebase/schema';
import { addDoc, updateDoc, deleteDoc, doc, getDocs, setDoc, getDoc } from 'firebase/firestore';
import { useNotificationStore } from './notificationStore';
import { useAuthStore } from './authStore';
import type { Category } from '../types';

export const useCategoryStore = defineStore('category', () => {
  // Estado
  const categories = ref<Record<string, string>>({});
  const categoryOrder = ref<string[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const notificationStore = useNotificationStore();
  const authStore = useAuthStore();

  // Cargar categorías desde Firestore
  const fetchCategories = async () => {
    // Check if user is authenticated
    if (!authStore.user) {
      console.error('Attempted to fetch categories without authentication');
      return;
    }

    try {
      loading.value = true;
      
      // 1. Primero obtener todas las categorías existentes
      const snapshot = await getDocs(categoriesCollection);
      const fetchedCategories: Record<string, string> = {};
      
      snapshot.forEach(doc => {
        fetchedCategories[doc.id] = doc.data().name;
      });

      categories.value = fetchedCategories;
      const existingCategoryIds = Object.keys(fetchedCategories);

      // 2. Intentar cargar el orden de categorías
      const orderDocRef = doc(metadataCollection, 'category_order');
      const orderDoc = await getDoc(orderDocRef);
      
      let currentOrder: string[] = [];
      let needsUpdate = false;

      if (orderDoc.exists()) {
        const orderData = orderDoc.data();
        const savedOrder = orderData?.order;
        
        // Verificar si el orden guardado es válido
        if (Array.isArray(savedOrder) && savedOrder.length > 0) {
          currentOrder = savedOrder;
        } else {
          // El documento existe pero el orden está vacío o no es válido
          needsUpdate = true;
          currentOrder = existingCategoryIds;
        }
      } else {
        // El documento no existe
        needsUpdate = true;
        currentOrder = existingCategoryIds;
      }

      // 3. Sincronizar el orden con las categorías existentes
      if (currentOrder.length > 0) {
        // Filtrar categorías que ya no existen
        const validOrder = currentOrder.filter(id => existingCategoryIds.includes(id));
        
        // Añadir nuevas categorías que no están en el orden
        const newCategories = existingCategoryIds.filter(id => !validOrder.includes(id));
        
        if (newCategories.length > 0 || validOrder.length !== currentOrder.length) {
          needsUpdate = true;
          currentOrder = [...validOrder, ...newCategories];
        } else {
          currentOrder = validOrder;
        }
      }

      // 4. Actualizar el estado local
      categoryOrder.value = currentOrder;

      // 5. Guardar en Firestore si es necesario
      if (needsUpdate && currentOrder.length > 0) {
        try {
          await setDoc(orderDocRef, {
            order: currentOrder,
            lastUpdated: new Date()
          });
          console.log('Category order updated in Firestore:', currentOrder);
        } catch (updateError) {
          console.error('Error updating category order:', updateError);
          // No lanzar error aquí para no interrumpir la carga de categorías
        }
      }

    } catch (err) {
      console.error('Error fetching categories:', err);
      error.value = 'Error al cargar categorías';
      notificationStore.error('Error al cargar las categorías');
    } finally {
      loading.value = false;
    }
  };

  // Guardar orden de categorías
  const saveCategoryOrder = async (newOrder: string[]) => {
    try {
      loading.value = true;
      const orderDocRef = doc(metadataCollection, 'category_order');
      
      await setDoc(orderDocRef, {
        order: newOrder,
        lastUpdated: new Date()
      });
      
      categoryOrder.value = newOrder;
      notificationStore.success('Orden de categorías guardado correctamente');
    } catch (error) {
      console.error('Error saving category order:', error);
      notificationStore.error('Error al guardar el orden de las categorías');
      throw error;
    } finally {
      loading.value = false;
    }
  };

  // Agregar categoría
  const addCategory = async (name: string) => {
    try {
      loading.value = true;
      const docRef = await addDoc(categoriesCollection, { name });
      
      // Actualizar estado local
      categories.value = {
        ...categories.value,
        [docRef.id]: name
      };
      
      // Agregar nueva categoría al orden
      const newOrder = [...categoryOrder.value, docRef.id];
      await saveCategoryOrder(newOrder);
      
      return docRef.id;
    } catch (err) {
      console.error('Error adding category:', err);
      throw new Error('Error al agregar categoría');
    } finally {
      loading.value = false;
    }
  };

  // Actualizar categoría
  const updateCategoryName = async (id: string, newName: string) => {
    try {
      loading.value = true;
      await updateDoc(doc(categoriesCollection, id), { name: newName });
      categories.value = {
        ...categories.value,
        [id]: newName
      };
    } catch (err) {
      console.error('Error updating category:', err);
      throw new Error('Error al actualizar categoría');
    } finally {
      loading.value = false;
    }
  };

  // Eliminar categoría
  const deleteCategory = async (id: string) => {
    try {
      loading.value = true;
      await deleteDoc(doc(categoriesCollection, id));
      
      // Actualizar estado local
      const { [id]: removed, ...rest } = categories.value;
      categories.value = rest;
      
      // Actualizar orden eliminando la categoría
      const newOrder = categoryOrder.value.filter(catId => catId !== id);
      await saveCategoryOrder(newOrder);
    } catch (err) {
      console.error('Error deleting category:', err);
      throw new Error('Error al eliminar categoría');
    } finally {
      loading.value = false;
    }
  };

  return {
    categories,
    categoryOrder,
    loading,
    error,
    fetchCategories,
    addCategory,
    updateCategoryName,
    deleteCategory,
    saveCategoryOrder
  };
});